import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  DollarSign, Search, TrendingUp, Calendar, ArrowUpRight,
  ArrowDownRight, CreditCard, Clock, CheckCircle, AlertTriangle,
  XCircle, RefreshCw, Filter, Ban, Printer, Send, FileText, Pencil, Trash2,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const PAYMENT_STATUS = {
  paid: { className: 'sa-badge-paid', label: 'Paid' },
  pending: { className: 'sa-badge-pending', label: 'Pending' },
  failed: { className: 'sa-badge-failed', label: 'Failed' },
  refunded: { className: 'sa-badge-refunded', label: 'Refunded' },
};

/* ============================================================
   PAYMENTS PAGE
   ============================================================ */
export default function SuperAdminPayments() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleSendEmail = async (inv) => {
    try {
      const { emailAPI } = await import('../../services/api');
      await emailAPI.send({
        to: inv.company_email || 'admin@company.com',
        subject: `Subscription Invoice #${inv.id} — LedgerPro`,
        html: `<h2>Subscription Invoice</h2><p>Company: ${inv.company_name}</p><p>Plan: ${inv.plan_name}</p><p>Amount: $${inv.amount}</p><p>Status: ${inv.status}</p>`,
      });
      toast.success(`Invoice sent to ${inv.company_name}`);
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    }
  };

  const load = useCallback(async () => {
    try {
      const [inv, s] = await Promise.all([
        subscriptionsAPI.invoices(),
        subscriptionsAPI.stats().catch(() => null),
      ]);
      setInvoices(inv);
      setStats(s);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Filter invoices
  const handleMarkPaid = async (inv) => {
    if (!window.confirm(`Mark invoice #${inv.id} as paid?\nAmount: $${inv.amount}\nCompany: ${inv.company_name}`)) return;
    try {
      await subscriptionsAPI.payInvoice(inv.id, { payment_method: 'manual' });
      toast.success('Payment recorded!');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleMarkUnpaid = async (inv) => {
    if (!window.confirm(`Mark invoice #${inv.id} as unpaid?`)) return;
    try {
      await subscriptionsAPI.unpayInvoice(inv.id);
      toast.success('Invoice marked as unpaid');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice #${inv.id} for ${inv.company_name}?\n\nThis cannot be undone!`)) return;
    try {
      await subscriptionsAPI.deleteInvoice(inv.id);
      toast.success('Invoice deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const filtered = invoices.filter(inv => {
    if (search && !inv.company_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && inv.status !== statusFilter) return false;
    return true;
  });

  // Revenue summaries
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const thisYear = String(now.getFullYear());

  const todayRevenue = invoices.filter(i => i.status === 'paid' && i.created_at?.startsWith(today)).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const monthRevenue = invoices.filter(i => i.status === 'paid' && i.created_at?.startsWith(thisMonth)).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const yearRevenue = invoices.filter(i => i.status === 'paid' && i.created_at?.startsWith(thisYear)).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const pendingCount = invoices.filter(i => i.status === 'pending').length;
  const failedCount = invoices.filter(i => i.status === 'failed').length;

  return (
    <div>
      {/* Revenue summary */}
      <div className="sa-stats-grid">
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#16a34a' }}><DollarSign size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">Today</div>
            <div className="sa-stat-value">{fmt(todayRevenue)}</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#3b82f6' }}><TrendingUp size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">This Month</div>
            <div className="sa-stat-value">{fmt(monthRevenue)}</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#8b5cf6' }}><Calendar size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">This Year</div>
            <div className="sa-stat-value">{fmt(yearRevenue)}</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#0ea5e9' }}><CreditCard size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">Total Revenue</div>
            <div className="sa-stat-value">{fmt(totalRevenue)}</div>
            <div className="sa-stat-sub">all time</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#16a34a' }}><CheckCircle size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">Paid</div>
            <div className="sa-stat-value">{paidCount}</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#f59e0b' }}><Clock size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">Pending</div>
            <div className="sa-stat-value">{pendingCount}</div>
          </div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-icon" style={{ background: '#ef4444' }}><XCircle size={18} /></div>
          <div className="sa-stat-body">
            <div className="sa-stat-label">Failed</div>
            <div className="sa-stat-value">{failedCount}</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <div className="sa-search-box" style={{ width: 260 }}>
            <Search size={14} />
            <input placeholder="Search by company..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="sa-filter-tabs">
            {[
              { key: '', label: 'All' },
              { key: 'paid', label: 'Paid' },
              { key: 'pending', label: 'Pending' },
              { key: 'failed', label: 'Failed' },
              { key: 'refunded', label: 'Refunded' },
            ].map(f => (
              <button key={f.key} className={`sa-filter-tab ${statusFilter === f.key ? 'active' : ''}`} onClick={() => setStatusFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <span className="sa-toolbar-title">{filtered.length} payments</span>
      </div>

      {/* Table */}
      <div className="sa-card">
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Company</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Cycle</th>
                <th>Status</th>
                <th>Period</th>
                <th>Date</th>
                <th className="sa-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="sa-empty-row"><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : filtered.length === 0 ? (
                <tr className="sa-empty-row"><td colSpan={8}>No payment records found</td></tr>
              ) : filtered.map(inv => {
                const st = PAYMENT_STATUS[inv.status] || PAYMENT_STATUS.pending;
                return (
                  <tr key={inv.id}>
                    <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>#{inv.id}</td>
                    <td>
                      <div className="sa-company-cell">
                        <strong>{inv.company_name}</strong>
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>{inv.plan_name}</td>
                    <td style={{ fontSize: 13, fontWeight: 700, color: inv.status === 'paid' ? '#16a34a' : inv.status === 'failed' ? '#dc2626' : '#1e293b' }}>
                      {fmt(inv.amount)}
                    </td>
                    <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{inv.billing_cycle}</td>
                    <td><span className={`sa-badge-status ${st.className}`}>{st.label}</span></td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>
                      {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                    </td>
                    <td style={{ fontSize: 12 }}>{fmtDate(inv.created_at)}</td>
                    <td className="sa-text-right">
                      <div className="sa-row-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
                        <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate('/super-admin/invoices/' + inv.id)} title="View & Edit Invoice" style={{ color: '#3b82f6', borderColor: '#3b82f6' }}>
                          <FileText size={12} /> Invoice
                        </button>
                        <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={() => navigate('/super-admin/payments/' + inv.id)} title="Process Payment">
                          <CreditCard size={12} /> Pay
                        </button>
                        {inv.status !== 'paid' && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleMarkPaid(inv)} title="Mark as Paid">
                            <CheckCircle size={12} /> Paid
                          </button>
                        )}
                        {inv.status === 'paid' && (
                          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => handleMarkUnpaid(inv)} title="Mark as Unpaid" style={{ color: '#dc2626', borderColor: '#dc2626' }}>
                            <Ban size={12} /> Undo
                          </button>
                        )}
                        <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => handleDelete(inv)} title="Delete Invoice" style={{ color: '#dc2626' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
